import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GroupIcon from '@mui/icons-material/Group';

import WarningIcon from '@mui/icons-material/Warning';
import {Button, Chip, Paper, Stack, Tooltip, Typography} from '@mui/material';
import Grid from '@mui/material/Grid';
import {number, object} from 'prop-types';
import React, {memo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import CollectionToolbar from './CollectionToolbar.jsx';
import {useGetReservedProductsForCollection} from '@api/quotaReservations.js';
import {setTourIndex} from '@app/actions/tour';
import {trackEvent} from '@app/analytics';
import AreaIndicator from '@app/components/common/AreaIndicator';
import {TOURS, TOUR_STAGES} from '@app/constants';
import useCollectionParams from '@app/hooks/useCollectionParams';
import formatNumber from '@app/util/format-number';
import ReservationsChip from '@components/common/ReservationsChip.jsx';

const CollectionItem = ({collection, productCount}) => {
  const dispatch = useDispatch();
  const {setCollection} = useCollectionParams();
  const isTourRunning = useSelector(
    state => state.tour.run && state.tour.tour === TOURS.COLLECTION_TOUR_PT1
  );

  const {data: reservedProducts, isSuccess} =
    useGetReservedProductsForCollection(collection.id);

  const handleOpenClick = () => {
    trackEvent('Collection Item', {
      action: 'Opened',
      collectionId: collection.id,
    });

    setCollection(collection.id);

    if (isTourRunning) {
      dispatch(setTourIndex(1));
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        paddingBottom: 1,
        paddingLeft: 1,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
      }}
    >
      <Grid container>
        <Grid
          size={{
            sm: 9,
            md: 9,
            lg: 9,
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            noWrap
            sx={{
              fontWeight: 500,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {`${collection.title}`}
          </Typography>
        </Grid>
        <Grid
          size={{
            sm: 3,
            md: 3,
            lg: 3,
          }}
        >
          <CollectionToolbar
            collection={collection}
            hasProducts={productCount > 0}
          />
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 12,
            lg: 12,
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" sx={{color: 'text.secondary'}}>
              Features: {formatNumber(collection.feature_count, null, '0,0')}
            </Typography>
            <Typography sx={{color: 'text.secondary'}}>•</Typography>
            <Typography variant="body2" sx={{color: 'text.secondary'}}>
              Total:
            </Typography>
            <Typography variant="body2" sx={{color: 'text.secondary'}}>
              <AreaIndicator squareMeters={collection.area} />
            </Typography>
          </Stack>
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 12,
            lg: 12,
          }}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: 1,
          }}
        >
          <Stack direction="row" spacing={1}>
            {isSuccess && reservedProducts?.length > 0 && productCount > 0 && (
              <ReservationsChip
                reservationsProductNames={reservedProducts.map(p => p.title)}
                showLabel
              />
            )}

            {collection.permissions?.is_owner && <Chip label="Owner" />}

            {collection.permissions?.shared && (
              <Tooltip title="Shared" placement="top">
                <Chip icon={<GroupIcon fontSize="small" color="action" />} />
              </Tooltip>
            )}

            {collection.feature_count === 0 && (
              <Tooltip
                title="Add features to enable collection view actions"
                placement="top-end"
              >
                <WarningIcon color="warning" fontSize="small" />
              </Tooltip>
            )}
          </Stack>

          <Button
            onClick={() => handleOpenClick()}
            color="primary"
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            sx={{
              height: theme => theme.spacing(3.75),
              minWidth: theme => theme.spacing(10),
            }}
            data-tour={TOUR_STAGES.OPEN_COLLECTION}
          >
            Open
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

CollectionItem.propTypes = {
  collection: object.isRequired,
  productCount: number,
};

export default memo(CollectionItem);
